import React, { useEffect, useState } from 'react';
import API from './api';

function getSemiannualPeriod(date = new Date()) {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  return month <= 6 ? `${year}-01` : `${year}-07`;
}
function formatSemiannualLabel(period) {
  if (!period) return '';
  const [year, m] = period.split('-');
  if (m === '01') return `Jan–Jun ${year}`;
  if (m === '07') return `Jul–Dec ${year}`;
  return period;
}

export default function StudentAchievement({ mode = "teacher" }) {
  const isAdmin = mode === "admin";
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [activeTab, setActiveTab] = useState('monthly'); // 'monthly' | 'semiannual'
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [achievementText, setAchievementText] = useState('');
  const [goalText, setGoalText] = useState('');
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  // Učitaj studente (BE već sortira, ali još jednom robustno sortiramo i formatiramo "Prezime, Ime")
  useEffect(() => {
    API.get('/achievements/students').then(res => {
      const list = (res.data || []).slice().sort((a, b) => {
        const la = (a.last_name || '').localeCompare(b.last_name || '', 'en', { sensitivity: 'base' });
        return la !== 0 ? la : (a.first_name || '').localeCompare(b.first_name || '', 'en', { sensitivity: 'base' });
      });
      setStudents(list);
    });
  }, []);

  // Učitaj sve periode gde postoje izveštaji + podesi current kao default
  useEffect(() => {
    setPeriods([]); setSelectedPeriod(''); setAchievementText(''); setGoalText('');
    if (selectedStudent && activeTab) {
      API.get(`/achievements/${selectedStudent}/${activeTab}/periods`)
        .then(res => {
          const ps = res.data || [];
          const now = new Date();
          const currentPeriod = activeTab === 'monthly' ? now.toISOString().slice(0, 7) : getSemiannualPeriod(now);
          if (!ps.includes(currentPeriod)) ps.unshift(currentPeriod);
          setPeriods(ps);
          setSelectedPeriod(currentPeriod);
        });
    }
  }, [selectedStudent, activeTab]);

  // Učitaj achievement & goal tekst za period
  useEffect(() => {
    setAchievementText(''); setGoalText('');
    if (selectedStudent && activeTab && selectedPeriod) {
      API.get(`/achievements/${selectedStudent}/${activeTab}/${selectedPeriod}`)
        .then(res => {
          const row = res.data || {};
          setAchievementText(row.achievement_text || '');
          setGoalText(row.goal_txt || '');
        });
    }
  }, [selectedStudent, activeTab, selectedPeriod]);

  const handleSave = async () => {
    if (!selectedStudent || !activeTab || !selectedPeriod) return;
    if (!achievementText.trim() && !goalText.trim()) return; // oba prazna – nema šta da se sačuva
    setSaving(true);
    try {
      await API.post(`/achievements/${selectedStudent}/${activeTab}/${selectedPeriod}`, {
        achievement_text: achievementText,   // može biti i prazan string (clearing)
        goal_txt: goalText                   // isto
      });
      setSuccessMsg('Saved!');
      setTimeout(() => setSuccessMsg(''), 2000);
      // osveži periods (nije nužno, ali neka)
      API.get(`/achievements/${selectedStudent}/${activeTab}/periods`).then(res => setPeriods(res.data || []));
      // i ponovno učitaj trenutni zapis
      const { data } = await API.get(`/achievements/${selectedStudent}/${activeTab}/${selectedPeriod}`);
      setAchievementText(data?.achievement_text || '');
      setGoalText(data?.goal_txt || '');
    } catch {
      alert('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto bg-white rounded shadow p-6 mt-6">
      <h2 className="text-lg font-bold mb-3">
        Student Achievements & Goals{isAdmin ? " (Admin)" : ""}
      </h2>

      <label className="block mb-1 font-semibold">Select student:</label>
      <select
        className="border p-2 rounded w-full mb-3"
        value={selectedStudent}
        onChange={e => setSelectedStudent(e.target.value)}
      >
        <option value="">-- Choose student --</option>
        {students.map(s => (
          <option key={s.id} value={s.id}>
            {(s.last_name || '').trim()}, {(s.first_name || '').trim()}
          </option>
        ))}
      </select>

      <div className="flex space-x-4 mb-4">
        <button
          className={`px-4 py-2 rounded ${activeTab === 'monthly' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          onClick={() => setActiveTab('monthly')}
        >Monthly</button>
        <button
          className={`px-4 py-2 rounded ${activeTab === 'semiannual' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          onClick={() => setActiveTab('semiannual')}
        >Semiannual</button>
      </div>

      {selectedStudent && (
        <>
          <label className="block mb-1 font-semibold">
            Select {activeTab === 'monthly' ? 'month' : 'semiannual period'}:
          </label>
          <select
            className="border p-2 rounded w-full mb-3"
            value={selectedPeriod}
            onChange={e => setSelectedPeriod(e.target.value)}
          >
            <option value="">-- Choose period --</option>
            {periods.map(p => (
              <option key={p} value={p}>
                {activeTab === 'semiannual' ? formatSemiannualLabel(p) : p}
                {p === (activeTab === 'monthly' ? new Date().toISOString().slice(0, 7) : getSemiannualPeriod()) ? ' (current)' : ''}
              </option>
            ))}
          </select>
        </>
      )}

      {selectedStudent && selectedPeriod && (
        <>
          <label className="block mb-1 font-semibold">Achievements ({activeTab === 'semiannual' ? formatSemiannualLabel(selectedPeriod) : selectedPeriod}):</label>
          <textarea
            className="border rounded w-full p-2 min-h-[120px] mb-3"
            value={achievementText}
            onChange={e => setAchievementText(e.target.value)}
            placeholder="Describe achievements for this student in the selected period..."
          />

          <label className="block mb-1 font-semibold">Goals ({activeTab === 'semiannual' ? formatSemiannualLabel(selectedPeriod) : selectedPeriod}):</label>
          <textarea
            className="border rounded w-full p-2 min-h-[120px] mb-3"
            value={goalText}
            onChange={e => setGoalText(e.target.value)}
            placeholder="Set goals for this student in the selected period..."
          />

          <button
            className="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-60"
            onClick={handleSave}
            disabled={saving || (!achievementText.trim() && !goalText.trim())}
          >
            {saving ? "Saving..." : "Save"}
          </button>
          {successMsg && <div className="mt-2 text-green-600">{successMsg}</div>}
        </>
      )}
    </div>
  );
}



